import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Search, Bug, BarChart3, Phone, Mail, MapPin, ChevronRight, Menu, X, AlertTriangle, CheckCircle } from "lucide-react";

const SpectraSecurityLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          element.classList.add('animate-fade-in');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Button click handlers
  const handleGetAssessment = () => {
    setModalType('assessment');
    setShowModal(true);
  };

  const handleEmergencyResponse = () => {
    setModalType('emergency');
    setShowModal(true);
  };

  const handleLearnMore = (service) => {
    setModalType('service');
    setShowModal(true);
  };

  const handleScheduleConsultation = () => {
    // Close modal first
    setShowModal(false);
    setModalType('');
    
    // Then scroll to contact section
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      contactSection?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleReportIncident = () => {
    setModalType('incident');
    setShowModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setSubmitError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message
        })
      });

      if (res.ok) {
        setFormSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
        setTimeout(() => setFormSubmitted(false), 4000);
      } else {
        const err = await res.json().catch(() => ({}));
        setSubmitError(err.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
  };

  // Modal component
  const Modal = ({ type, onClose }) => {
    const modalContent = {
      assessment: {
        title: "Get Security Assessment",
        content: "Our security experts will conduct a comprehensive assessment of your current cybersecurity posture and provide actionable recommendations.",
        action: "Schedule Assessment",
        icon: <Shield className="w-8 h-8 text-blue-400" />
      },
      emergency: {
        title: "Emergency Response",
        content: "If you're experiencing a security incident, our emergency response team is available 24/7 to provide immediate assistance.",
        action: "Call Emergency Hotline",
        icon: <AlertTriangle className="w-8 h-8 text-red-400" />
      },
      service: {
        title: "Learn More About Our Services",
        content: "Discover how our comprehensive cybersecurity services can protect your business from evolving digital threats.",
        action: "Contact Our Team",
        icon: <Search className="w-8 h-8 text-blue-400" />
      },
      incident: {
        title: "Report Security Incident",
        content: "Report a security incident immediately. Our incident response team will take immediate action to contain and mitigate the threat.",
        action: "Report Incident Now",
        icon: <AlertTriangle className="w-8 h-8 text-red-400" />
      }
    };

    const content = modalContent[type];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-slate-800 rounded-xl p-8 max-w-md mx-4 border border-slate-700"
        >
          <div className="text-center">
            <div className="flex justify-center mb-4">
              {content.icon}
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white">{content.title}</h3>
            <p className="text-slate-300 mb-6 leading-relaxed">{content.content}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={type === 'emergency' || type === 'incident' ? () => window.open('tel:+2203011517') : () => {
                  onClose();
                  setTimeout(() => {
                    const contactSection = document.getElementById('contact');
                    contactSection?.scrollIntoView({ behavior: 'smooth' });
                  }, 300);
                }}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                {content.action}
              </button>
              <button
                onClick={onClose}
                className="border border-slate-600 text-slate-300 hover:bg-slate-700 px-6 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const services = [
    {
      icon: <Search className="w-12 h-12 text-red-500" />,
      title: "Digital Forensics & Incident Response",
      description: "Comprehensive digital investigation and rapid incident response to protect your business from cyber threats and data breaches.",
      image: "https://images.unsplash.com/photo-1593407089396-93f0c7a575f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwZm9yZW5zaWNzfGVufDB8fHxibHVlfDE3NTI2NTY4NTd8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Bug className="w-12 h-12 text-red-500" />,
      title: "Penetration Testing",
      description: "Ethical hacking and vulnerability assessments to identify security weaknesses before malicious actors exploit them.",
      image: "https://images.unsplash.com/photo-1581488066648-56a585ab6767?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwZm9yZW5zaWNzfGVufDB8fHxibHVlfDE3NTI2NTY4NTd8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-red-500" />,
      title: "Security Analysis",
      description: "Deep security audits and risk assessments to strengthen your cybersecurity posture and compliance requirements.",
      image: "https://images.pexels.com/photos/10988021/pexels-photo-10988021.jpeg"
    }
  ];

  const stats = [
    { number: "20+", label: "Clients Protected" },
    { number: "99.9%", label: "Threat Detection Rate" },
    { number: "24/7", label: "Monitoring & Support" },
    { number: "10+", label: "Years of Experience" }
  ];

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img src="/maktran-mark.png" alt="Maktran Cyber logo" className="h-9 w-auto mr-3" />
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Maktran Cyber
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="#home" className="text-slate-300 hover:text-white transition-colors duration-200">Home</a>
                <a href="#services" className="text-slate-300 hover:text-white transition-colors duration-200">Services</a>
                <a href="#about" className="text-slate-300 hover:text-white transition-colors duration-200">About</a>
                <a href="#contact" className="text-slate-300 hover:text-white transition-colors duration-200">Contact</a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-slate-300 hover:text-white"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-md border-b border-slate-800/50">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <a href="#home" className="block px-3 py-2 text-slate-300 hover:text-white">Home</a>
              <a href="#services" className="block px-3 py-2 text-slate-300 hover:text-white">Services</a>
              <a href="#about" className="block px-3 py-2 text-slate-300 hover:text-white">About</a>
              <a href="#contact" className="block px-3 py-2 text-slate-300 hover:text-white">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900">
          <motion.div
            style={{ y: heroY, opacity: heroOpacity }}
            className="absolute inset-0"
          >
            <img 
              src="https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg"
              alt="Cybersecurity Background"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 to-slate-900/80"></div>
          </motion.div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <motion.h1 
              className="text-5xl md:text-7xl font-bold leading-tight"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-white to-red-500 bg-clip-text text-transparent">
                Defend Your Digital
              </span>
              <br />
              <span className="text-white">Fortress</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Elite cybersecurity consultancy providing comprehensive digital forensics, penetration testing, and security analysis to protect your business from evolving cyber threats.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <button 
                onClick={handleGetAssessment}
                className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                <span className="flex items-center">
                  Get Security Assessment
                  <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
              <button 
                onClick={handleEmergencyResponse}
                className="group border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105">
                <span className="flex items-center">
                  Emergency Response
                  <Phone className="ml-2 h-5 w-5 transition-transform group-hover:scale-110" />
                </span>
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center animate-on-scroll"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">{stat.number}</div>
                <div className="text-slate-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16 animate-on-scroll"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-red-500 bg-clip-text text-transparent">
                Our Security Services
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Comprehensive cybersecurity solutions tailored to protect your digital assets and business operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="group relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 hover:bg-slate-800/70 transition-all duration-300 transform hover:scale-105 border border-slate-700/50 hover:border-blue-500/50 animate-on-scroll"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="relative overflow-hidden rounded-lg mb-6 h-48">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    {service.icon}
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-blue-400 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <button 
                  onClick={() => handleLearnMore(service.title)}
                  className="text-blue-400 hover:text-white font-semibold transition-colors duration-200 flex items-center group">
                  Learn More
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="animate-on-scroll"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1660644808219-1f103401bc85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5fGVufDB8fHxibHVlfDE3NTI1MzgwODl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Cybersecurity Team"
                  className="rounded-xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-xl"></div>
              </div>
            </motion.div>
            
            <motion.div
              className="space-y-6 animate-on-scroll"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 to-red-500 bg-clip-text text-transparent">
                  Why Choose Maktran Cyber?
                </span>
              </h2>
              
              <p className="text-xl text-slate-300 leading-relaxed">
                With over 10 years of experience in cybersecurity, our team of elite security professionals has protected reputable companies and government agencies from the most sophisticated cyber threats.
              </p>
              
              <div className="space-y-4">
                {[
                  "Advanced threat detection and response capabilities",
                  "Certified security experts with industry-leading credentials",
                  "24/7 monitoring and incident response services",
                  "Comprehensive compliance and risk management solutions"
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-slate-300">{item}</span>
                  </motion.div>
                ))}
              </div>
              
              <button 
                onClick={handleScheduleConsultation}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                Schedule Consultation
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16 animate-on-scroll"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-red-500 bg-clip-text text-transparent">
                Secure Your Business Today
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Don't wait for a cyber attack. Contact our security experts for a comprehensive assessment and protection plan.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              className="space-y-8 animate-on-scroll"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                <h3 className="text-2xl font-bold mb-6">Get In Touch</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold">Emergency Hotline</div>
                      <div className="text-slate-300">+220-301-1517</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold">Email</div>
                      <div className="text-slate-300">info@maktrancyber.com</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold">Address</div>
                      <div className="text-slate-300">Serving Clients Globally from Banjul, GMB</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
                <h4 className="text-xl font-semibold mb-4 text-red-400">24/7 Emergency Response</h4>
                <p className="text-slate-300 mb-4">
                  Experiencing a security incident? Our emergency response team is available around the clock to help contain and mitigate cyber threats.
                </p>
                <button 
                  onClick={handleReportIncident}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105">
                  Report Incident
                </button>
              </div>
            </motion.div>

            <motion.div
              className="animate-on-scroll"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50">
                <h3 className="text-2xl font-bold mb-6">Request Consultation</h3>
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Your Name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Your Company"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      required
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Tell us about your security needs..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={formSubmitted || isSubmitting}
                    data-testid="contact-submit-button"
                    className={`w-full px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                      formSubmitted 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white disabled:opacity-70 disabled:cursor-not-allowed'
                    }`}
                  >
                    {formSubmitted ? (
                      <span className="flex items-center justify-center">
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Message Sent Successfully!
                      </span>
                    ) : isSubmitting ? (
                      'Sending...'
                    ) : (
                      'Send Message'
                    )}
                  </button>
                  {submitError && (
                    <p className="text-red-400 text-sm mt-3" data-testid="contact-error">{submitError}</p>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <Modal type={modalType} onClose={closeModal} />
      )}

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center">
                <img src="/maktran-logo-transparent.png" alt="Maktran Cyber" className="h-16 w-auto" />
              </div>
              <p className="text-slate-400">
                Elite cybersecurity consultancy protecting businesses from digital threats.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Digital Forensics</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Penetration Testing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security Analysis</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Incident Response</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/about-us" data-testid="footer-about-link" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/careers" data-testid="footer-careers-link" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/blog" data-testid="footer-blog-link" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400">
                <li>+220-301-1517</li>
                <li>info@maktrancyber.com</li>
                <li>Serving Clients Globally</li>
                <li>From Banjul, GMB</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p>
                &copy; 2026 Maktran Cyber. All rights reserved. |{' '}
                <Link
                  to="/privacy"
                  data-testid="footer-privacy-link"
                  className="hover:text-white transition-colors underline-offset-2 hover:underline"
                >
                  Privacy Policy
                </Link>{' '}
                |{' '}
                <Link
                  to="/terms"
                  data-testid="footer-terms-link"
                  className="hover:text-white transition-colors underline-offset-2 hover:underline"
                >
                  Terms of Service
                </Link>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SpectraSecurityLanding;
